// src/pages/ConfirmPasswordResetPage.tsx
import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { confirmPasswordReset, clearAuthStatus } from '../features/auth/authSlice';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // ADDED: Import useTranslation
import { Typography, Box, Button, CircularProgress, Alert, Link } from '@mui/material';
import AuthLayout from '../layout/AuthLayout';
import PasswordField from '../components/common/PasswordField';
import { validatePassword } from '../utils/validation'; // UPDATED: Import validation utility

const ConfirmPasswordResetPage: React.FC = () => {
    const { t } = useTranslation(); // ADDED: Initialize translation
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { token } = useParams<{ token: string }>();

    const { status, error, message } = useAppSelector((state) => state.auth);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        dispatch(clearAuthStatus());
    }, [dispatch]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLocalError(null);
        dispatch(clearAuthStatus());

        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setLocalError(passwordError);
            return;
        }

        if (newPassword !== confirmPassword) {
            setLocalError(t('passwords_do_not_match'));
            return;
        }
        if (!token) {
            setLocalError(t('reset_token_missing'));
            return;
        }

        dispatch(confirmPasswordReset({ token, newPassword }));
    };

    const isLoading = status.auth === 'loading';
    const isSucceeded = status.auth === 'succeeded' && !!message;

    return (
        <AuthLayout>
            {isSucceeded ? (
                <>
                    <Typography component="h1" variant="h5">{t('password_reset_success')}</Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 2 }}>
                        {t('password_reset_success_instructions')}
                    </Typography>
                    {message && <Alert severity="success" sx={{ width: '100%', mt: 2 }}>{message}</Alert>}
                    <Box textAlign="center" sx={{ mt: 2 }}>
                        <Link component={RouterLink} to="/login" color="inherit">
                            {t('back_to_sign_in')}
                        </Link>
                    </Box>
                </>
            ) : (
                <>
                    <Typography component="h1" variant="h5">{t('set_new_password')}</Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 2 }}>
                        {t('set_new_password_instructions')}
                    </Typography>

                    {localError && <Alert severity="warning" sx={{ width: '100%', mt: 2 }}>{localError}</Alert>}
                    {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                        <PasswordField
                            name="newPassword"
                            label={t('new_password')}
                            value={newPassword}
                            onChange={(e: any) => setNewPassword(e.target.value)}
                            autoFocus
                        />
                        <PasswordField
                            name="confirmPassword"
                            label={t('confirm_new_password')}
                            value={confirmPassword}
                            onChange={(e: any) => setConfirmPassword(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2,
                                bgcolor: 'inverse.main',
                                color: 'inverse.contrastText',
                                '&:hover': { bgcolor: 'inverse.hover.main' }
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('reset_password')}
                        </Button>
                    </Box>

                    <Box textAlign="center" sx={{ mt: 1 }}>
                        <Link component={RouterLink} to="/login" color="inherit">
                            {t('back_to_sign_in')}
                        </Link>
                    </Box>
                </>
            )}
        </AuthLayout>
    );
};

export default ConfirmPasswordResetPage;